import { useEffect, useRef, useMemo } from "react";

const StarField = () => {
  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);

  const particles = useMemo(() => {
    const stars = Array.from({ length: 80 }, () => ({
      x: Math.random() * 2000,
      y: Math.random() * 2000,
      size: Math.random() * 1.8 + 0.3,
      speedX: (Math.random() - 0.5) * 0.12,
      speedY: -(Math.random() * 0.15 + 0.03),
      opacity: Math.random() * 0.45 + 0.1,
      pulse: Math.random() * Math.PI * 2,
      pulseSpeed: Math.random() * 0.006 + 0.002,
      type: "star",
    }));
    // Ambient cyan motes — larger, slower, faint glow
    const motes = Array.from({ length: 15 }, () => ({
      x: Math.random() * 2000,
      y: Math.random() * 2000,
      size: Math.random() * 2.5 + 1.5,
      speedX: (Math.random() - 0.5) * 0.08,
      speedY: (Math.random() - 0.5) * 0.06,
      opacity: Math.random() * 0.12 + 0.04,
      pulse: Math.random() * Math.PI * 2,
      pulseSpeed: Math.random() * 0.004 + 0.001,
      type: "mote",
    }));
    return [...stars, ...motes];
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((p) => {
        p.x += p.speedX;
        p.y += p.speedY;
        p.pulse += p.pulseSpeed;

        if (p.x < -20) p.x = canvas.width + 20;
        if (p.x > canvas.width + 20) p.x = -20;
        if (p.y < -20) p.y = canvas.height + 20;
        if (p.y > canvas.height + 20) p.y = -20;

        const currentOpacity = p.opacity * (0.5 + 0.5 * Math.sin(p.pulse));

        if (p.type === "mote") {
          // Soft cyan glow mote
          const gradient = ctx.createRadialGradient(
            p.x, p.y, 0, p.x, p.y, p.size * 6
          );
          gradient.addColorStop(0, `rgba(0, 240, 255, ${currentOpacity * 0.6})`);
          gradient.addColorStop(0.4, `rgba(0, 240, 255, ${currentOpacity * 0.15})`);
          gradient.addColorStop(1, "transparent");
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size * 6, 0, Math.PI * 2);
          ctx.fillStyle = gradient;
          ctx.fill();

          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size * 0.7, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(200, 245, 255, ${currentOpacity * 0.8})`;
          ctx.fill();
        } else {
          // Regular star
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(220, 235, 255, ${currentOpacity})`;
          ctx.fill();

          if (p.size > 1.2) {
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size * 2.5, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(200, 230, 255, ${currentOpacity * 0.06})`;
            ctx.fill();
          }
        }
      });

      animFrameRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      window.removeEventListener("resize", resize);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [particles]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="star-field"
      className="fixed inset-0 z-0 pointer-events-none"
      style={{ opacity: 0.9 }}
    />
  );
};

export default StarField;
